import dynamic from 'next/dynamic';

import type {
  SanityFullWidthMediaSource,
  SanityFullWidthVideo,
} from '#/lib/sanity/queries/fullWidthVideo';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { useMediaQuery } from '#/lib/useMatchMedia';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';
import { FormattedText } from '#/components/FormattedText';
import { Headline } from '#/components/Headline';
import { Media } from '#/components/Media';
import { Overline } from '#/components/Overline';
import { VideoLoop } from '#/components/VideoLoop';

import { ConsentBoundary } from './ConsentBoundary';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

const mediaFrameClassName =
  'relative w-full overflow-clip rounded-16 bg-grayscale-06';

function hasConfiguredMedia(
  media?: SanityFullWidthVideo['mobileMedia'],
): media is SanityFullWidthMediaSource {
  if (!media?.mediaType) return false;
  if (media.mediaType === 'youtube') {
    return Boolean(media.videoUrl && media.thumbnailImage);
  }
  if (media.mediaType === 'videoLoop') {
    return Boolean(media.videoLoop);
  }
  if (media.mediaType === 'image') {
    return Boolean(media.image);
  }
  return false;
}

export function FullWidthMedia(
  props: SanityFullWidthVideo & PageLayoutSharedProps,
) {
  const isFirst = props.sectionIndex === 0;
  const isDark = isDarkSectionTheme(props.theme);
  const accentClass = getSectionThemeAccent(props.theme);
  const [matchesDesktop, hasRun] = useMediaQuery({ screen: 'm' });
  const isMobile = hasRun && !matchesDesktop;
  const hideMedia = isMobile && Boolean(props.hideMediaOnMobile);
  const hasHeader = Boolean(
    props.eyebrow ||
      props.title ||
      props.description?.length ||
      props.cta?.href,
  );

  return (
    <div
      className={cn('p-main', getSectionThemeSurface(props.theme), {
        'py-60 m:py-100': !isFirst,
        'mt-[calc(var(--header-height)*-1)] pb-60 m:pb-100 pt-[calc(var(--header-height)+5rem)]':
          isFirst,
      })}
    >
      <div className="mx-auto flex max-w-[1280px] flex-col items-center">
        {hasHeader ? (
          <div
            className={cn(
              'flex w-full max-w-[800px] flex-col items-center gap-48 text-center',
              { 'mb-50': !hideMedia },
            )}
          >
            {props.eyebrow ? (
              <Overline
                className={cn('type-eyebrow', accentClass)}
                dataSanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  'eyebrow',
                )}
              >
                {props.eyebrow}
              </Overline>
            ) : null}

            {props.title || props.description?.length ? (
              <div className="flex w-full flex-col items-center gap-20">
                {props.title ? (
                  <Headline
                    value={props.title}
                    variant={3}
                    highlightClass={accentClass}
                    className="type-heading3 w-full text-center m:!text-40 l:!text-40"
                    dataSanity={createSanityFieldDataAttribute(
                      props.sanitySource,
                      'title',
                    )}
                  />
                ) : null}
                {props.description?.length ? (
                  <FormattedText
                    value={props.description}
                    className={cn(
                      'w-full items-center text-center type-paragraph',
                      isDark ? 'text-body-cloud' : 'text-body',
                    )}
                    dataSanity={createSanityFieldDataAttribute(
                      props.sanitySource,
                      'description',
                    )}
                  />
                ) : null}
              </div>
            ) : null}

            {props.cta?.href ? (
              <Button
                href={props.cta.href}
                target={props.cta.target}
                variant="primary"
                theme={getSectionButtonTheme(props.theme)}
                data-sanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  'cta',
                )}
              >
                {props.cta.title}
              </Button>
            ) : null}
          </div>
        ) : null}

        {hideMedia ? null : (
          <FullWidthMediaContent {...props} isMobile={isMobile} />
        )}
      </div>
    </div>
  );
}

function FullWidthMediaContent({
  isMobile,
  ...props
}: SanityFullWidthVideo &
  PageLayoutSharedProps & {
    isMobile: boolean;
  }) {
  const desktopMedia: SanityFullWidthMediaSource = {
    mediaType: props.mediaType,
    thumbnailImage: props.thumbnailImage,
    videoUrl: props.videoUrl,
    videoLoop: props.videoLoop,
    image: props.image,
  };

  const mobileMedia = props.mobileMedia;
  const useMobileMedia = isMobile && hasConfiguredMedia(mobileMedia);
  const activeMedia: SanityFullWidthMediaSource = useMobileMedia
    ? mobileMedia
    : desktopMedia;

  return (
    <FullWidthMediaSource
      media={activeMedia}
      sectionIndex={props.sectionIndex}
      sanitySource={props.sanitySource}
      sanityFieldPrefix={useMobileMedia ? 'mobileMedia.' : ''}
    />
  );
}

function FullWidthMediaSource({
  media,
  sectionIndex,
  sanitySource,
  sanityFieldPrefix,
}: {
  media: SanityFullWidthMediaSource;
  sectionIndex: SanityFullWidthVideo['sectionIndex'];
  sanitySource: PageLayoutSharedProps['sanitySource'];
  sanityFieldPrefix: string;
}) {
  if (media.mediaType === 'image' && media.image) {
    return (
      <div
        className="w-full overflow-clip rounded-16"
        data-sanity={createSanityFieldDataAttribute(
          sanitySource,
          `${sanityFieldPrefix}image`,
        )}
      >
        <Media
          media={media.image}
          aspect={true}
          className="w-full"
          mediaClassName="object-contain"
          sizes={['100vw', { m: '1280px' }]}
        />
      </div>
    );
  }

  if (media.mediaType === 'videoLoop' && media.videoLoop) {
    return (
      <div
        className="w-full overflow-clip rounded-16"
        data-sanity={createSanityFieldDataAttribute(
          sanitySource,
          `${sanityFieldPrefix}videoLoop`,
        )}
      >
        <VideoLoop
          video={media.videoLoop}
          aspect={true}
          className="w-full"
          mediaClassName="object-contain"
          sizes={['100vw', { m: '1280px' }]}
          sectionIndex={sectionIndex ?? undefined}
        />
      </div>
    );
  }

  if (
    media.mediaType !== 'youtube' ||
    !media.videoUrl ||
    !media.thumbnailImage
  ) {
    return null;
  }

  return (
    <div className={cn(mediaFrameClassName, 'aspect-[16/9]')}>
      <ConsentBoundary urlsRequiredForRender={[media.videoUrl]}>
        <ReactPlayer
          width="100%"
          height="100%"
          url={media.videoUrl}
          className="absolute inset-0 size-full"
          controls={true}
          playsinline={true}
          playing={true}
          light={
            <div className="absolute inset-0 size-full">
              <Media
                media={media.thumbnailImage}
                className="size-full"
                placeholder={true}
                sizes={['100vw', { m: '1280px' }]}
              />
              <div className="absolute inset-0 bg-charcoal/20" />
            </div>
          }
          playIcon={
            <div className="absolute left-1/2 top-1/2 grid size-78 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-cloud text-charcoal transition hover:scale-110 m:size-100">
              <svg
                className="w-28 translate-x-3 m:w-32 m:translate-x-4"
                viewBox="0 0 33 39"
                fill="none"
              >
                <path
                  d="M1.25 1.74648L32 19.5L1.25 37.2535L1.25 1.74648Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          }
        />
      </ConsentBoundary>
    </div>
  );
}
